import React, { useCallback, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { getCroppedImageFile } from '../utils/cropImage';
import { useNotify } from '../context/NotifyContext';

interface ImageCropModalProps {
  /** Object URL (or data URL) of the picked file, to be displayed for cropping. */
  imageSrc: string;
  /** Original file name/type, so the cropped result uploads like a normal picked file. */
  fileName: string;
  mimeType: string;
  /** width / height. Pass undefined to allow free-form cropping (no fixed ratio). */
  aspect?: number;
  /** Human-readable hint, e.g. "Recommended: 800 × 600px (4:3)". */
  recommendedHint?: string;
  onCancel: () => void;
  onConfirm: (file: File) => void;
}

/**
 * Blocking "must crop before upload" step. There is intentionally no way to upload the
 * original, un-cropped image — Cancel discards the pick entirely, Crop & Use uploads the
 * cropped result. This keeps every uploaded image consistent with the aspect ratio a given
 * section/layout expects.
 */
const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageSrc,
  fileName,
  mimeType,
  aspect,
  recommendedHint,
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);
  const { notify } = useNotify();

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixelsValue: Area) => {
    setCroppedAreaPixels(croppedAreaPixelsValue);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const file = await getCroppedImageFile(imageSrc, croppedAreaPixels, fileName, mimeType);
      onConfirm(file);
    } catch (error) {
      console.error('Failed to crop image', error);
      notify.error('Could not process the crop. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white dark:bg-nova-bg1 rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-nova-border">
          <h3 className="text-base font-semibold text-nova-text1">Crop image</h3>
          {recommendedHint && <p className="text-xs text-nova-text3 mt-1">{recommendedHint}</p>}
        </div>

        <div className="relative w-full h-80 bg-nova-bg2">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect || 1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        <div className="px-5 py-4 flex items-center gap-3">
          <span className="text-xs text-nova-text3 flex-shrink-0">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
        </div>

        <div className="px-5 py-4 border-t border-nova-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={processing || !croppedAreaPixels}
            className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50"
          >
            {processing ? 'Processing...' : 'Crop & Use'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
